/**
 * ResetPassword — public page behind a staff-sent, single-use reset link
 * (spec 2026-09-28-platform-password-reset §4.3). Sets the new password only; the person then signs in
 * normally (password + emailed code). Stores nothing in the browser: there is no session.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Alert, Button, Link, TextField, Typography } from '@mui/material';

const API_URL = import.meta.env.VITE_API_URL || '/api';
const INVALID = 'This link has expired or was already used. Ask your administrator for a new one.';

/** The token rides in the URL FRAGMENT (`#token=…`), which never reaches the server or its logs. A
 *  `?token=` query is deliberately NOT read: only the fragment form is a reset link. */
function fragmentToken(): string {
  const h = window.location.hash.replace(/^#/, '');
  return (new URLSearchParams(h).get('token') ?? '').trim();
}

export default function ResetPassword() {
  // Read once, before the effect below strips the fragment from the address bar.
  const [token] = useState(fragmentToken);
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(token ? null : INVALID);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  // Don't leave the live token visible in the address bar (or in history / a shared screenshot).
  useEffect(() => {
    if (window.location.hash) {
      window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
    }
  }, []);

  // Defence in depth while mounted (the Activate page's M-9 rule): no Referer on anything fetched from here.
  useEffect(() => {
    const existing = document.querySelector('meta[name="referrer"]');
    const previousContent = existing ? existing.getAttribute('content') : null;
    const meta = existing ?? document.createElement('meta');
    if (!existing) {
      meta.setAttribute('name', 'referrer');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', 'no-referrer');
    return () => {
      if (previousContent !== null) meta.setAttribute('content', previousContent);
      else meta.parentNode?.removeChild(meta);
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (pw.length < 12) { setError('Password must be at least 12 characters.'); return; }
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_URL}/auth/reset-password`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token, password: pw }) });
      const body = await r.json().catch(() => ({}));
      if (r.ok && body.success) setDone(true); else setError(body.error ?? INVALID);
    } catch { setError('Could not reach PRISM. Check your connection and try again.'); }
    finally { setBusy(false); }
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
      <Box sx={{ maxWidth: 440, width: '100%' }}>
        <Typography variant="h5" component="h1" gutterBottom>Set a new PRISM password</Typography>
        {done ? (
          <Alert role="status" severity="success" sx={{ mb: 2 }}>
            ✓ Password set — <Link component={RouterLink} to="/login" replace>sign in</Link>. We will email you a sign-in code.
          </Alert>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            {error && <Alert role="alert" severity="error" sx={{ mb: 2 }}>⚠ {error}</Alert>}
            {token && (
              <>
                <TextField label="New password" type="password" autoComplete="new-password" fullWidth margin="normal"
                  value={pw} onChange={e => setPw(e.target.value)} helperText="At least 12 characters." />
                <TextField label="Confirm password" type="password" autoComplete="new-password" fullWidth margin="normal"
                  value={confirm} onChange={e => setConfirm(e.target.value)} />
                <Button type="submit" variant="contained" disabled={busy} sx={{ mt: 2 }}>Set new password</Button>
              </>
            )}
          </form>
        )}
      </Box>
    </Box>
  );
}
